import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  useGetAggregatedRequestTypeSettingsPreviewQuery,
  useGetRequestTypeSettingsQuery,
} from '@/redux/slices/requestApiSlice';
import { useSettingDisplayName } from '@/hooks/useSettingDisplayName';
import { RequestTypeSlugEnum } from '@/types/requests';
import {
  filterVisibleSettings,
  isGoogleLicenseSetting,
  sortSettingsWithChildren,
} from '@/components/Applications/ApplicationDetails/Settings/settingsUtils';
import type { AutomationSettings } from '@/components/Applications/ApplicationDetails/Settings/types';

type UseResolvedRowSettingsArgs = {
  organizationId: string;
  applicationKey: string;
  /** All of the groups this employee is being onboarded into (not just the
   * groups that happen to provision this specific app) -- a group can carry a
   * settings override for an app even if it isn't the group granting access
   * to it, so every group membership needs to be considered. */
  groupIds: string[];
};

// Arc's Table only mounts `renderSubComponent` while a row is actually
// expanded (it's conditionally rendered, not just hidden), so there's no need
// to gate these queries behind an `isExpanded`/`skip` flag ourselves -- simply
// mounting this hook is the lazy-fetch trigger.
export const useResolvedRowSettings = ({
  organizationId,
  applicationKey,
  groupIds,
}: UseResolvedRowSettingsArgs) => {
  const {
    data: settingsPreview,
    isFetching: isFetchingPreview,
    isError: isErrorPreview,
  } = useGetAggregatedRequestTypeSettingsPreviewQuery({
    organizationId,
    applicationKey,
    requestTypeKey: RequestTypeSlugEnum.CREATE_ACCOUNT,
    groupIds,
  });

  const {
    data: requestTypeSettings,
    isFetching: isFetchingMeta,
    isError: isErrorMeta,
  } = useGetRequestTypeSettingsQuery({
    applicationKey,
    requestTypeKey: RequestTypeSlugEnum.CREATE_ACCOUNT,
  });

  // TODO: Remove this filter once Google Workspace license assignment is
  // fully released (matches the same gate in ApplicationSettingsPane.tsx).
  const { googleLicenseAssignment } = useFlags();
  const getSettingDisplayName = useSettingDisplayName();

  const orderedRTS = useMemo(() => {
    const results = (requestTypeSettings?.results ?? []).filter(
      rts =>
        googleLicenseAssignment ||
        !isGoogleLicenseSetting(rts.name, applicationKey)
    );
    return sortSettingsWithChildren(results);
  }, [requestTypeSettings, googleLicenseAssignment, applicationKey]);

  const settings = useMemo((): AutomationSettings => {
    if (orderedRTS.length === 0) return {};

    const displayNames: Record<string, string> = {};
    const types: Record<string, string> = {};
    orderedRTS.forEach(rts => {
      displayNames[rts.name] = getSettingDisplayName(applicationKey, rts);
      types[rts.name] = rts.type;
    });

    const rawSettings = settingsPreview?.settings ?? {};

    const built: AutomationSettings = {};
    orderedRTS.forEach(rts => {
      if (rawSettings[rts.name] === undefined) return;
      built[rts.name] = {
        label: displayNames[rts.name] ?? rts.name,
        value: rawSettings[rts.name],
        type: types[rts.name],
      };
    });

    return filterVisibleSettings(built, orderedRTS);
  }, [orderedRTS, settingsPreview, applicationKey, getSettingDisplayName]);

  return {
    settings,
    hasSettings: orderedRTS.length > 0,
    isLoading: isFetchingPreview || isFetchingMeta,
    isError: isErrorPreview || isErrorMeta,
  };
};
